import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ErrorMessage, FieldArray, Formik } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import CustomPhoneInput from "../../../components/input/phone-input";
import "react-phone-input-2/lib/style.css";
import { RxCross2 } from "react-icons/rx";
import { FaPlus } from "react-icons/fa";
import { roleTypes } from "../../../data/roleType";

const ManufacturerAdd = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phoneNumbers: [""],
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const data = {
      name: values.name,
      email: values.email,
      address: values?.address,
      phoneNumbers: values.phoneNumbers,
    };
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      const response = await axios.post(
        `${url.backendURL}manufacturer/`,
        data,
        config
      );

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "manufacturer-add",
      });
      setErrors({});
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
        navigate(`/dashboard/app/manufacturer-list`);
      }, 2000);
    } catch (err) {
      handleError(err, "manufacturer-add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (response.data.error === "jwt expired" || response?.data?.error === "Unauthorized. User not found") {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const handleReset = (resetForm) => {
    resetForm();
  };

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string()
        .min(1, "Name must be at least 1 character long")
        .max(70, "Name must not exceed 70 characters")
        .trim()
        .test(
          "starts-with-letter-or-alphanumeric",
          "Name must start with a letter or alphanumeric character",
          (value) => {
            if (!value) return false;

            // Regex to ensure name starts with a letter or alphanumeric character, contains at least one letter, and doesn't start with special characters or numbers
            return (
              /^[a-zA-Z]/.test(value) &&
              /[a-zA-Z]/.test(value) &&
              !/^[\d]+$/.test(value)
            );
          }
        )
        .required("Name is required"),

      email: Yup.string()
        .trim()
        .required("Email is required")
        .email("Enter a valid email address")
        .test(
          "no-spaces",
          "Email may not contain any spaces at the beginning or end",
          (value) => !value || value.trim() === value
        )
        .test("min-domain-segments", "Enter a valid email address", (value) => {
          if (!value) return true;
          const parts = value.split("@");
          if (parts.length !== 2) return false;
          const domainSegments = parts[1].split(".");
          return (
            domainSegments.length >= 2 &&
            domainSegments.every((s) => s.length >= 2)
          );
        })
        .test("realistic-email", "Enter a valid email address", (value) => {
          if (!value) return true;
          const [local, domain] = value.split("@");
          if (!local || !domain) return false;

          if (/^\d+$/.test(local)) return false;
          if (local.length > 64) return false;

          if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(domain)) return false;

          return true;
        }),

      phoneNumbers: Yup.array()
        .of(
          Yup.string()
            .trim()
            .matches(
              /^[+]?[\d-]{10,15}$/,
              "Phone number must be 10 to 15 digits with optional + or - symbols"
            )
            .test(
              "trim",
              "Phone number may not contain any spaces at the beginning or end",
              (value) => value === (value && value.trim())
            )
            .required("Phone number is required")
        )
        .min(1, "At least one phone number is required"),
      address: Yup.string()
        .matches(
          /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
          "Address must contain at least one letter or number"
        )
        .min(1, "Address must be at least 1 character long")
        .max(255, "Address must not exceed 255 characters")
        .trim()
        .required("Address is required"),
    });

  return (
    <>
      <div>
        <Formik
          enableReinitialize={true}
          initialValues={formData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            handleBlur,
            touched,
            setFieldValue,
            handleSubmit,
            isSubmitting,
            resetForm,
          }) => (
            <Form onSubmit={handleSubmit}>
              <FocusError />
              <Row>
                <Col xl="12" lg="12" ref={alertRef}>
                  <Card>
                    <Card.Header className="d-flex justify-content-between">
                      <div
                        className="breadcrumb header-title"
                        style={{ cursor: "pointer" }}
                      >
                        <h4
                          onClick={() =>
                            navigate("/dashboard/app/manufacturer-list")
                          }
                          className="text-primary card-title"
                          style={{ cursor: "pointer" }}
                        >
                          <span>Manufacturer</span>
                        </h4>
                        <h4 className="mx-2 card-title">{">"}</h4>
                        <h4 className="card-title">Add</h4>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                      <div className="new-manufacturer-info">
                        <div className="row">
                          <InputField
                            className="col-md-6 form-group"
                            type="text"
                            name="name"
                            placeholder="Enter Name"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            asterick={true}
                            errorMessage={errors?.name}
                            label="Name"
                          />
                          <InputField
                            className="col-md-6 form-group"
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={values.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            asterick={true}
                            errorMessage={errors?.email}
                            label="Email"
                          />

                          <InputField
                            className="col-md-6 form-group"
                            type="text"
                            name="address"
                            placeholder="Enter address"
                            value={values.address}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            asterick={true}
                            errorMessage={errors?.address}
                            label="Address"
                          />
                          <Row>
                            <Form.Label htmlFor="phoneNumbers">
                              Phone Number{" "}
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <FieldArray
                              name="phoneNumbers"
                              render={(arrayHelpers) => (
                                <>
                                  {/* Iterate through phone numbers and group every two items in a row */}
                                  {values.phoneNumbers.map((phone, index) => {
                                    const isNewRow = index % 2 === 0;

                                    return (
                                      <React.Fragment key={index}>
                                        <Col
                                          md={6}
                                          className="d-flex flex-row align-items-start "
                                        >
                                          <Col md={10} className="mb-3">
                                            <CustomPhoneInput
                                              id={`phoneNumbers.${index}`}
                                              name={`phoneNumbers.${index}`}
                                              value={phone}
                                              onChange={(value) =>
                                                setFieldValue(
                                                  `phoneNumbers.${index}`,
                                                  value
                                                )
                                              }
                                              onBlur={handleBlur}
                                              placeholder="Enter phone number"
                                              country="us"
                                            />
                                            <ErrorMessage
                                              name={`phoneNumbers.${index}`}
                                              component="div"
                                              className="text-danger"
                                            />
                                          </Col>

                                          {values.phoneNumbers.length > 1 &&
                                            index !== 0 && (
                                              <Button
                                                variant="danger"
                                                className="d-flex align-items-center justify-content-center ms-2"
                                                onClick={() =>
                                                  arrayHelpers.remove(index)
                                                }
                                              >
                                                <RxCross2
                                                  style={{
                                                    width: "22px",
                                                    height: "22px",
                                                  }}
                                                  color="white"
                                                />
                                              </Button>
                                            )}
                                          {index === 0 && (
                                            <Button
                                              variant="primary"
                                              disabled={
                                                values.phoneNumbers.length > 2
                                              }
                                              className="d-flex align-items-center justify-content-center ms-2"
                                              onClick={() =>
                                                arrayHelpers.push("")
                                              }
                                            >
                                              <FaPlus
                                                style={{
                                                  width: "22px",
                                                  height: "22px",
                                                }}
                                                color="white"
                                              />
                                            </Button>
                                          )}
                                        </Col>
                                      </React.Fragment>
                                    );
                                  })}
                                </>
                              )}
                            />
                          </Row>
                        </div>
                        <hr />
                        <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                          <Button
                            type="button"
                            className="me-2 me-md-5 px-3 px-md-4"
                            variant="btn btn-light"
                            onClick={() => handleReset(resetForm)} // Reset the form
                          >
                            Reset
                          </Button>

                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            variant="btn btn-primary"
                            className="me-2 me-md-5 px-3 px-md-4"
                          >
                            Submit
                          </Button>
                          <Button
                            type="button"
                            className=" px-3 px-md-4"
                            variant="btn btn-danger"
                            onClick={() =>
                              navigate("/dashboard/app/manufacturer-list")
                            }
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default ManufacturerAdd;
